import { getDecimalCount } from '@/lib/currencies';
import { useTranslation } from 'react-i18next';
import { usePlans } from '@/hooks/use-plans';

interface PriceSummaryProps {
  planId: string;
}

export const PriceSummary = ({ planId }: PriceSummaryProps) => {
  // @ts-ignore - Type instantiation depth limitation
  const { t, i18n } = useTranslation();
  const { plans } = usePlans();

  const currentPlan = plans.find(p => p.id === planId);
  const planPricing = currentPlan?.pricing as any;

  if (!planPricing || !currentPlan) {
    return null;
  }

  const currency = planPricing.currency || 'USD';
  const totalAmount = planPricing.current || 0;
  const originalAmount = planPricing.original || 0;

  const perMonthAmount = currentPlan.duration.months > 0
    ? (totalAmount / currentPlan.duration.months).toFixed(getDecimalCount(currency))
    : totalAmount.toFixed(getDecimalCount(currency));

  return (
    <div className="flex flex-row md:justify-center lg:flex-col items-center lg:items-start">

      {/* Large graphic/illustration */}
      <div className="relative mb-0 mr-5 lg:w-full lg:mr-0 lg:mb-4 flex flex-col justify-center items-center lg:items-start">
        {/* shadow-[0_0_36px_rgba(148,163,184,0.25)] */}
        <div className="relative w-36 lg:w-48 aspect-square rounded-xl overflow-hidden shadow-[0_0_64px_rgba(148,163,184,0.25)] hover:lg:-rotate-3 lg:-translate-x-2 transition-all duration-500 ease-out">
          <img
            src={currentPlan.id === 'quarterly'
              ? 'https://cdn-site.clarymind.com/product-page-app/plans/pro-3_month-product-image.jpg'
              : 'https://cdn-site.clarymind.com/product-page-app/plans/pro-annual-product-image-v2.jpg'
            }
            alt="Plan Preview"
            className="w-full h-full object-cover"
          />
          {/* <div className={`absolute inset-0 bg-gradient-to-br ${currentPlan.style.gradientFrom} ${currentPlan.style.gradientVia ? currentPlan.style.gradientVia : ''} ${currentPlan.style.gradientTo} opacity-95`} />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-tl-lg" />
          <div className="absolute top-4 right-4 flex flex-col gap-1">
            <div className="w-8 h-2 bg-yellow-400/60 rounded" />
            <div className="w-10 h-2 bg-yellow-400/60 rounded" />
            <div className="w-12 h-2 bg-yellow-400/60 rounded" />
            <div className="w-10 h-2 bg-yellow-400/60 rounded" />
          </div> */}
        </div>
      </div>

      {/* Plan name */}
      <div className="flex flex-col items-start w-fit">
        <div className="text-sm text-yellow-300 mb-2 mt-1">
          {/* @ts-ignore - Type instantiation depth limitation */}
          {t(currentPlan.name)}
        </div>

        {/* Current price - prominently displayed */}
        <div className="flex flex-wrap items-baseline gap-2 mb-2">
          <span className="text-2xl lg:text-4xl font-semibold text-white">
            {currency} {Number(totalAmount).toLocaleString()}
          </span>
          <span className="text-sm text-slate-400">
            {/* @ts-ignore - Type instantiation depth limitation */}
            / {t(currentPlan.unit)}
          </span>
        </div>

        <div className="flex flex-row flex-wrap mb-2">

          {/* monthly breakdown */}
          {originalAmount > 0 && (
            <div className="text-sm text-slate-400 mr-2">
              <span>
                ({currency} {Number(perMonthAmount).toLocaleString()} {/* @ts-ignore - Type instantiation depth limitation */}
                / {t('purchase.perMonth')})
              </span>
            </div>
          )}

          {/* Original price with strikethrough and monthly breakdown */}
          {originalAmount > 0 && (
            <div className="text-sm text-slate-500 mr-2">
              <span className="line-through">
                {currency} {Number(originalAmount).toLocaleString()} {/* @ts-ignore - Type instantiation depth limitation */}
                / {t(currentPlan.unit)}
              </span>
            </div>
          )}

        </div>
      </div>

      {/* Separator */}
      <div className="w-full h-px bg-slate-200 mt-8 mb-4 order-last lg:bg-slate-800 hidden lg:block" />

    </div>
  );
};
